/**
 * SPEC-TrainingSimulator §7.3a — markdown parsing for lessons (pure, no React
 * components here so simple-markdown.tsx stays Fast-Refresh friendly).
 * Subset: # / ## / ### headings, paragraphs, - and 1. lists, **bold**,
 * *italic*, `code`, [text](https://url). Output is React nodes / block
 * descriptors — never HTML strings.
 */

import type { ReactNode } from "react";

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g;

export function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(INLINE_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const idx = m.index;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    if (tok.startsWith("**")) out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={key++} style={{ background: "#F3F4F6", padding: "0 4px", borderRadius: 3, fontSize: "0.92em" }}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("*")) out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    else if (tok.startsWith("[")) {
      const close = tok.indexOf("](");
      const label = tok.slice(1, close);
      const href = tok.slice(close + 2, -1);
      out.push(<a key={key++} href={href} target="_blank" rel="noreferrer noopener" style={{ color: "#592F96", textDecoration: "underline" }}>{label}</a>);
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: "h"; level: 1 | 2 | 3; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

export function parseBlocks(md: string): Block[] {
  const lines = (md ?? "").replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => { if (para.length) { blocks.push({ kind: "p", text: para.join(" ") }); para = []; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) { flush(); blocks.push({ kind: "h", level: h[1].length as 1 | 2 | 3, text: h[2] }); continue; }
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    if (ul) {
      flush();
      const lastBlock = blocks[blocks.length - 1];
      if (lastBlock?.kind === "ul") lastBlock.items.push(ul[1]); else blocks.push({ kind: "ul", items: [ul[1]] });
      continue;
    }
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) {
      flush();
      const lastBlock = blocks[blocks.length - 1];
      if (lastBlock?.kind === "ol") lastBlock.items.push(ol[1]); else blocks.push({ kind: "ol", items: [ol[1]] });
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    para.push(line.trim());
  }
  flush();
  return blocks;
}

